import React from 'react';
import type { EpicDetail } from '../../../../../../shared/types/epics';
import { StatusBadge } from '../../../../components/ui';

/**
 * FORGE-318 (P1): make an epic's effective repo scope legible. A multi-repo
 * project epic starts across every attached repo; a confirmed narrower scope (a
 * `Scope:` directive at creation, or the requirements Q&A) narrows involved_repos
 * before planning. Surfacing the in-scope set here answers "what is this epic
 * actually scoped to" at a glance — the question that made epic #60 opaque.
 * Only meaningful for cross-repo epics; a single-repo epic's scope is its header repo.
 */
export const ScopeCard: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  if (epic.model !== 'cross-repo') return null;
  const repos = epic.involvedRepos ?? [];
  if (repos.length === 0) return null;

  const anchor = epic.repo;
  const narrowedToOne = repos.length === 1;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="epic-scope-card">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-semibold text-slate-900">Repo scope</span>
        <span className="text-xs text-slate-500">
          {repos.length} {repos.length === 1 ? 'repo' : 'repos'} in scope
        </span>
        {narrowedToOne && <StatusBadge tone="info">scoped to one repo</StatusBadge>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {repos.map((r) => (
          <span
            key={r}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 font-mono text-xs text-slate-700"
          >
            {r}
            {r === anchor && (
              <span className="text-[10px] uppercase tracking-wide text-slate-400">anchor</span>
            )}
          </span>
        ))}
      </div>
      <p className="mt-2 text-[11px] leading-snug text-slate-500">
        The repos this epic plans and builds against. A multi-repo project epic starts across
        all attached repos; a confirmed narrower scope (a <span className="font-mono">Scope:</span>{' '}
        directive or the requirements Q&amp;A) limits it to these.
      </p>
    </div>
  );
};
